import { z } from 'zod'
import { vector2, vector3 } from './utils'

/**
 * Set the isomorphic transform of an object
 */

export const BundleMonocleTransformDBModel = z.object({
  /// Translation in world space.
  translation: vector3.optional(),
  /// Rotation around the local origin.
  /// 3x3 matrix, column major.
  rotation: z.array(z.number()).length(9).optional(),
  /// Scale from local origin.
  scale: vector3.optional(),
})

/**
 * Intrinsic parameters that define the camera projection and distortions.
 * https://docs.opencv.org/2.4/modules/calib3d/doc/camera_calibration_and_3d_reconstruction.html
 */

export const BundleMonocleCameraIntrinsicDBModel = z.object({
  /// Focal Length scalar in camera coordinate space.
  focal_length: vector2,
  /// Center Point of camera in camera coordinate space.
  center_point: vector2,
  // --- camera distortion parameters ---
  k1: z.number().optional(),
  k2: z.number().optional(),
  k3: z.number().optional(),
  k4: z.number().optional(),
  k5: z.number().optional(),
  k6: z.number().optional(),
  p1: z.number().optional(),
  p2: z.number().optional(),
})

/**
 * Camera parameters, that define source region and pinhole camera model
 */

export const BundleMonocleCameraParametersDBModel = z.object({
  /// Camera frame width in video coordinate space.
  /// This establishes the camera coordinate space.
  width: z.number(),
  /// Camera frame height in video coordinate space.
  /// This establishes the camera coordinate space.
  height: z.number(),
  /// Start coordinate of frame rectangle in video coordinate space.
  start: vector2,
  /// Whether the intrinsic parameters have been applied already.
  undistorted: z.boolean(),
  /// Intrinsic parameters that define the camera projection and distortions.
  intrinsic: BundleMonocleCameraIntrinsicDBModel,
})

/**
 * Monocle RGBD Camera
 */

export const BundleMonocleVideoRGBDCameraModel = z.object({
  depth: BundleMonocleCameraParametersDBModel,
  color: BundleMonocleCameraParametersDBModel,
  transform: BundleMonocleTransformDBModel.optional(),
  extrinsic_color_from_depth: BundleMonocleTransformDBModel.optional(),
  depth_encoding: z.string(),
  unknown_depth_color: vector3.optional(),
  near: z.number(),
  far: z.number(),
})

/** RGBD atlas layout; storage paths stay on the owned bundle. */
export const MonocleVideoPlaybackDto = z.object({
  video_size: vector2,
  rgbd_cameras: z.array(BundleMonocleVideoRGBDCameraModel),
})
